/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { QualityScore } from '@/types/models.ts';
import Tooltip from 'rc-tooltip';

interface Props {
  className?: string;
  size?: string;
  qualityScore: QualityScore;
}

type Grade = 'A' | 'B' | 'C' | 'D' | 'E';

const gradeFromPct = (pct: number): Grade => {
  if (pct >= 80) {
    return 'A';
  }
  if (pct >= 60) {
    return 'B';
  }
  if (pct >= 40) {
    return 'C';
  }
  if (pct >= 20) {
    return 'D';
  }
  return 'E';
};

const GRADE_COLORS: Record<Grade, string> = {
  A: '#16a34a',
  B: '#65a30d',
  C: '#facc15',
  D: '#f97316',
  E: '#ef4444'
};

const ratingColorsItem = (score: number, target: number) => {
  if (score >= (2 / 3) * target) {
    return '#0e8a4a';
  }
  if (score >= (1 / 3) * target) {
    return '#FBC707';
  }
  return '#EF3120';
};

type LangRecord = { [key in 'fr' | 'en']: string };
type Category = { [key: string]: LangRecord };
const category: Category = {
  popularity: {
    fr: 'Popularité',
    en: 'Popularity'
  },
  open_source: {
    fr: 'Open Source',
    en: 'Open Source'
  },
  code_quality: {
    fr: 'Qualité de code',
    en: 'Code Quality'
  }
};

const DescriptionFR = ({ max, details }: QualityScore, lang: keyof LangRecord) => (
  <div className="w-full w-25rem text-sm line-height-3">
    <p className="">
      Ce score sur {max} représente une évaluation globale de la qualité et de la maturité d’un projet. Il se base sur
      plusieurs critères essentiels :
    </p>
    <ol>
      {Object.entries(details).map(([key, v]) => (
        <li key={key} className="mb-1">
          <strong className="capitalize">
            {category[key as keyof Category] ? category[key as keyof Category][lang] : key}
          </strong>
          <span> évalué sur {v.max}.</span>
          <br />
          <span> score obtenu : </span>
          <span style={{ backgroundColor: `${ratingColorsItem(v.value, v.max)}` }} className="inline-block ml-2 px-2">
            {v.value}
          </span>
        </li>
      ))}
    </ol>
    <p>
      Un score élevé indique un projet bien documenté, testé, ouvert à la contribution, et apprécié par sa communauté. À
      l’inverse, un score plus faible montre que certains éléments fondamentaux (comme un README détaillé, des tests, ou
      un pipeline de CI/CD) sont absents ou incomplets.
    </p>
    <p>En un coup d’œil, ce score vous aide à évaluer la fiabilité et la maintenabilité d’un projet.</p>
  </div>
);

const DescriptionEN = ({ max, details }: QualityScore, lang: keyof LangRecord) => (
  <div className="w-full w-29rem text-sm line-height-3">
    <p>
      This score out of {max} provides a global evaluation of a project's quality and maturity. It is based on several
      key criteria:
    </p>
    <ol>
      {Object.entries(details).map(([key, v]) => (
        <li key={key} className="my-1">
          <strong className="capitalize">
            {category[key as keyof Category] ? category[key as keyof Category][lang] : key}
          </strong>
          <span> graded out of {v.max}.</span>
          <br />
          <span> score obtained : </span>
          <span style={{ backgroundColor: ratingColorsItem(v.value, v.max) }} className="inline-block ml-2 px-2">
            {v.value}
          </span>
        </li>
      ))}
    </ol>
    <p>
      A high score indicates a project that is well-documented, tested, open to contributions, and appreciated by its
      community. Conversely, a lower score shows that certain key aspects (such as a detailed README, test coverage, or
      a CI/CD pipeline) may be absent or incomplete.
    </p>
    <p>At a glance, this score helps you assess a project's reliability and maintainability.</p>
  </div>
);

const description: { [key: string]: typeof DescriptionEN } = {
  fr: DescriptionFR,
  en: DescriptionEN
};

const Pill = styled.span<{ $color: string; $isYellow: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.12rem 0.18rem 0.12rem 0.5rem;
  border-radius: 999px;
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 60%, #020617 100%);
  color: #f8fafc;
  font-size: 0.56rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border: 1px solid rgba(255, 255, 255, 0.06);
  cursor: help;
  white-space: nowrap;
  line-height: 1;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.08) inset,
    0 0 0 1px ${(p) => p.$color}33,
    0 2px 8px ${(p) => p.$color}33;

  & .grade {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 1.1rem;
    height: 1.1rem;
    border-radius: 999px;
    font-size: 0.65rem;

    color: ${(p) => (p.$isYellow ? '#1f2937' : '#fff')};
    background: radial-gradient(
      circle at 30% 25%,
      color-mix(in srgb, ${(p) => p.$color} 100%, #fff 35%) 0%,
      ${(p) => p.$color} 55%,
      color-mix(in srgb, ${(p) => p.$color} 70%, #000 30%) 100%
    );
    box-shadow:
      0 0 0 1.5px rgba(255, 255, 255, 0.12),
      0 1px 4px ${(p) => p.$color}80,
      0 0 8px ${(p) => p.$color}55;
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.25);
  }
`;

export const QualityBadge: React.FC<Props> = ({ className, qualityScore }) => {
  const {
    i18n: { language }
  } = useTranslation();

  const _language = language ?? 'fr';
  const { value, max } = qualityScore;
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  const grade = gradeFromPct(pct);
  const color = GRADE_COLORS[grade];

  return (
    <Tooltip placement="bottom" overlay={description[_language](qualityScore, _language as keyof LangRecord)}>
      <Pill
        className={className}
        $color={color}
        $isYellow={grade === 'C'}
        role="img"
        aria-label={`Quality ${pct}% (${grade})`}
      >
        <div className="flex items-center gap-1">
          <div className="label">Quality</div>
          <div className="pct">{pct}%</div>
        </div>
        <span className="grade">{grade}</span>
      </Pill>
    </Tooltip>
  );
};
